import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Plus, Trash2, Loader2, Gift, Users, Search, Award } from 'lucide-react';
import { toast } from 'sonner';

export const AdminLoyalty = () => {
  const { apiCall } = useAdmin();
  const [customers, setCustomers] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Add Points Dialog
  const [isPointsDialogOpen, setIsPointsDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [pointsForm, setPointsForm] = useState({ points: 0, reason: '' });
  
  // Add Discount Dialog
  const [isDiscountDialogOpen, setIsDiscountDialogOpen] = useState(false);
  const [discountForm, setDiscountForm] = useState({
    discount_type: 'percent',
    discount_value: 0,
    reason: '',
    valid_until: '',
  });
  
  // Add Reward Dialog
  const [isRewardDialogOpen, setIsRewardDialogOpen] = useState(false);
  const [rewardForm, setRewardForm] = useState({
    name: { ro: '', en: '' },
    points_required: 0,
    discount_type: 'percent',
    discount_value: 0,
    is_active: true,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [customersData, rewardsData] = await Promise.all([
        apiCall('/admin/loyalty/customers'),
        apiCall('/admin/loyalty/rewards'),
      ]);
      setCustomers(customersData || []);
      setRewards(rewardsData || []);
    } catch (err) {
      toast.error('Failed to fetch loyalty data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPoints = async () => {
    try {
      await apiCall('/admin/loyalty/add-points', {
        method: 'POST',
        body: JSON.stringify({
          customer_id: selectedCustomer.id,
          points: pointsForm.points,
          reason: pointsForm.reason,
        }),
      });
      toast.success(`Added ${pointsForm.points} points`);
      setIsPointsDialogOpen(false);
      setPointsForm({ points: 0, reason: '' });
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleAddDiscount = async () => {
    try {
      await apiCall('/admin/loyalty/customer-discount', {
        method: 'POST',
        body: JSON.stringify({
          customer_id: selectedCustomer.id,
          ...discountForm,
        }),
      });
      toast.success('Discount added to customer');
      setIsDiscountDialogOpen(false);
      setDiscountForm({ discount_type: 'percent', discount_value: 0, reason: '', valid_until: '' });
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleRemoveDiscount = async (discountId) => {
    try {
      await apiCall(`/admin/loyalty/customer-discount/${discountId}`, { method: 'DELETE' });
      toast.success('Discount removed');
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleCreateReward = async () => {
    try {
      await apiCall('/admin/loyalty/rewards', {
        method: 'POST',
        body: JSON.stringify(rewardForm),
      });
      toast.success('Reward created');
      setIsRewardDialogOpen(false);
      setRewardForm({ name: { ro: '', en: '' }, points_required: 0, discount_type: 'percent', discount_value: 0, is_active: true });
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteReward = async (rewardId) => {
    if (!window.confirm('Delete this reward?')) return;
    try {
      await apiCall(`/admin/loyalty/rewards/${rewardId}`, { method: 'DELETE' });
      toast.success('Reward deleted');
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const filteredCustomers = customers.filter(c =>
    `${c.first_name} ${c.last_name} ${c.email}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-loyalty">
      <div>
        <h2 className="text-2xl font-heading font-bold text-[#1A1625]">Loyalty Program</h2>
        <p className="text-muted-foreground">Manage customer loyalty points and rewards</p>
      </div>

      <Tabs defaultValue="customers">
        <TabsList>
          <TabsTrigger value="customers" className="flex items-center gap-2">
            <Users className="h-4 w-4" /> Customers
          </TabsTrigger>
          <TabsTrigger value="rewards" className="flex items-center gap-2">
            <Award className="h-4 w-4" /> Rewards
          </TabsTrigger>
        </TabsList>

        <TabsContent value="customers" className="space-y-4">
          {/* Search */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search customers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Customers List */}
          <Card>
            <CardContent className="p-0">
              {filteredCustomers.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">No customers found</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-3 px-4">Customer</th>
                        <th className="text-left py-3 px-4">Email</th>
                        <th className="text-center py-3 px-4">Points</th>
                        <th className="text-left py-3 px-4">Discounts</th>
                        <th className="text-right py-3 px-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCustomers.map((customer) => (
                        <tr key={customer.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium">{customer.first_name} {customer.last_name}</td>
                          <td className="py-3 px-4 text-muted-foreground">{customer.email}</td>
                          <td className="py-3 px-4 text-center">
                            <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 px-3 py-1 rounded-full font-semibold">
                              <Gift className="h-3 w-3" />
                              {customer.loyalty_points || 0}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {customer.active_discounts?.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {customer.active_discounts.map((d) => (
                                  <span key={d.id} className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">
                                    {d.discount_value}{d.discount_type === 'percent' ? '%' : ' RON'}
                                    <button onClick={() => handleRemoveDiscount(d.id)} className="hover:text-red-500">
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setSelectedCustomer(customer); setIsPointsDialogOpen(true); }}
                              className="mr-2"
                            >
                              <Plus className="h-3 w-3 mr-1" /> Points
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setSelectedCustomer(customer); setIsDiscountDialogOpen(true); }}
                            >
                              <Plus className="h-3 w-3 mr-1" /> Discount
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rewards" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={isRewardDialogOpen} onOpenChange={setIsRewardDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary">
                  <Plus className="mr-2 h-4 w-4" /> Add Reward Tier
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Reward Tier</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Name (RO)</Label>
                      <Input
                        value={rewardForm.name.ro}
                        onChange={(e) => setRewardForm({ ...rewardForm, name: { ...rewardForm.name, ro: e.target.value } })}
                        placeholder="e.g., Bronze"
                      />
                    </div>
                    <div>
                      <Label>Name (EN)</Label>
                      <Input
                        value={rewardForm.name.en}
                        onChange={(e) => setRewardForm({ ...rewardForm, name: { ...rewardForm.name, en: e.target.value } })}
                        placeholder="e.g., Bronze"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Points Required</Label>
                    <Input
                      type="number"
                      value={rewardForm.points_required}
                      onChange={(e) => setRewardForm({ ...rewardForm, points_required: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Discount Type</Label>
                      <Select
                        value={rewardForm.discount_type}
                        onValueChange={(v) => setRewardForm({ ...rewardForm, discount_type: v })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percent">Percentage</SelectItem>
                          <SelectItem value="fixed">Fixed Amount</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Discount Value</Label>
                      <Input
                        type="number"
                        value={rewardForm.discount_value}
                        onChange={(e) => setRewardForm({ ...rewardForm, discount_value: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button variant="outline" onClick={() => setIsRewardDialogOpen(false)}>Cancel</Button>
                    <Button className="bg-primary" onClick={handleCreateReward}>Create</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Reward Tiers</CardTitle>
            </CardHeader>
            <CardContent>
              {rewards.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No reward tiers yet. Create one to get started.</p>
              ) : (
                <div className="grid gap-4">
                  {rewards.map((reward) => (
                    <div key={reward.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <h3 className="font-medium">{reward.name?.en || reward.name?.ro}</h3>
                        <p className="text-sm text-muted-foreground">
                          {reward.points_required} points = {reward.discount_value}{reward.discount_type === 'percent' ? '%' : ' RON'} off
                        </p>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteReward(reward.id)} className="text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Points Dialog */}
      <Dialog open={isPointsDialogOpen} onOpenChange={setIsPointsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Points to {selectedCustomer?.first_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Points</Label>
              <Input
                type="number"
                value={pointsForm.points}
                onChange={(e) => setPointsForm({ ...pointsForm, points: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Reason</Label>
              <Input
                value={pointsForm.reason}
                onChange={(e) => setPointsForm({ ...pointsForm, reason: e.target.value })}
                placeholder="e.g., Purchase reward, Birthday bonus"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsPointsDialogOpen(false)}>Cancel</Button>
              <Button className="bg-primary" onClick={handleAddPoints}>Add Points</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Discount Dialog */}
      <Dialog open={isDiscountDialogOpen} onOpenChange={setIsDiscountDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Discount to {selectedCustomer?.first_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select
                  value={discountForm.discount_type}
                  onValueChange={(v) => setDiscountForm({ ...discountForm, discount_type: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentage</SelectItem>
                    <SelectItem value="fixed">Fixed Amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Value</Label>
                <Input
                  type="number"
                  value={discountForm.discount_value}
                  onChange={(e) => setDiscountForm({ ...discountForm, discount_value: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
            <div>
              <Label>Reason</Label>
              <Input
                value={discountForm.reason}
                onChange={(e) => setDiscountForm({ ...discountForm, reason: e.target.value })}
                placeholder="e.g., VIP customer, Complaint resolution"
              />
            </div>
            <div>
              <Label>Valid Until (optional)</Label>
              <Input
                type="date"
                value={discountForm.valid_until}
                onChange={(e) => setDiscountForm({ ...discountForm, valid_until: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsDiscountDialogOpen(false)}>Cancel</Button>
              <Button className="bg-primary" onClick={handleAddDiscount}>Add Discount</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLoyalty;
